import { View } from 'react-native';
import { twMerge } from 'tailwind-merge';

import { Progress, ProgressFilledTrack } from '@/components/ui/progress';
import { Text } from '@/components/ui/text';
import { getAccessibilityProps } from '@/util/accessibilityProps';

interface ProcessingProgressBarProps {
  /** Current progress in `[0, 1]`. Values outside the range are clamped. */
  progress: number;
  /** Optional status message rendered below the bar. Update as progress advances. */
  message?: string;
  /** Outer container styles. */
  className?: string;
  /** Message text styles. */
  messageClassName?: string;
  /** Override for the announced label. Defaults to `${message}…`. */
  accessibilityLabel?: string;
}

/**
 * Progress bar with an optional status message that updates as work advances.
 * Designed for indeterminate-feeling progress with discrete known stages
 * (e.g. backend pipeline statuses); the message slot below the bar surfaces
 * the human-readable stage label.
 *
 * The bar is the shared Gluestack `Progress` primitive — the same one the
 * reference-assistant searching rows use — which renders its fill on a plain
 * `View` and so paints correctly on native (a Reanimated-animated fill with a
 * `className` background renders blank on native; that was the original
 * "blank processing bar" bug). The fill width snaps to each stage value, so
 * there is no animation to gate behind a reduced-motion check.
 *
 * `Progress` owns the `progressbar` role and `aria-value*` semantics; the
 * accessibility label is forwarded to it and the status message is a polite
 * live region so stage changes are announced. The outer `View` is layout only
 * and intentionally carries no progressbar role (doing so would create a
 * second, redundant progressbar node).
 */
export function ProcessingProgressBar({
  progress,
  message,
  className,
  messageClassName,
  accessibilityLabel,
}: ProcessingProgressBarProps) {
  const clampedProgress = Math.max(0, Math.min(1, progress));

  const announcedLabel = accessibilityLabel ?? (message ? `${message}…` : undefined);

  const labelProps = announcedLabel ? getAccessibilityProps({ label: announcedLabel }) : undefined;

  return (
    <View className={className}>
      <Progress value={Math.round(clampedProgress * 100)} size="xs" {...labelProps}>
        <ProgressFilledTrack />
      </Progress>
      {message ? (
        <Text
          size="xs"
          className={twMerge('mt-1 italic text-typography-500', messageClassName)}
          accessibilityLiveRegion="polite"
          aria-live="polite">
          {message}…
        </Text>
      ) : null}
    </View>
  );
}
